import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LobbyTableInfo } from '@draughts/shared';
import { variantShortLabel } from '../../utils/variant-labels';
import LobbyTableRow from './LobbyTableRow';
import AvvisoSenzaRete from '../shared/AvvisoSenzaRete';

interface Props {
  tables: LobbyTableInfo[];
  myTableId: string | null;
  /** Passed down to each row: a guest cannot sit at a "solo valida" table. */
  isGuest: boolean;
  /**
   * The variant the player has chosen upstairs — the one whose tables belong at
   * the top. Required, not optional: there is exactly one caller, so letting the
   * compiler check the wiring costs nothing and a silently missing prop would
   * turn the whole grouping into a no-op nobody would notice.
   */
  selectedVariant: string;
  busy: boolean;
  /**
   * La presenza e' scaduta: questa lista e' vuota perche' non si sa piu' niente,
   * non perche' non ci sia nessun tavolo. Cambia solo l'avviso dello stato vuoto
   * — ed e' tutto quello che serve, perche' i tavoli li ha gia' svuotati lo
   * store.
   */
  presenzaScaduta: boolean;
  /** Il dispositivo DICHIARA di non avere rete: distingue i due avvisi. */
  senzaRete: boolean;
  onSit: (tableId: string) => void;
  onCancel: () => void;
}

/**
 * The open tables, the chosen variant first.
 *
 * Inside each of the two groups the order is the server's — oldest first, which
 * IS the queue — so the grouping only lifts to the top the tables the quick
 * button could actually hand you. It matches on `variant` + `timeControl` +
 * `kind === 'quick'` (`findOldestQuick`), so the first row still is not
 * literally the one "3+2" would give you; what changes is that the rows above it
 * are no longer ones that button would never look at.
 *
 * When the chosen variant has nothing waiting, the other variants are still
 * offered — with a line saying so first, because a full list otherwise reads as
 * "there is a game for you here" when there isn't.
 *
 * Your own table is lifted OUT of that arrangement and pinned first, above the
 * scroller: it is the one row you cannot sit at, and the FIFO would bury it at
 * the bottom precisely because you just opened it.
 */
export default function LobbyTableList({
  tables,
  myTableId,
  isGuest,
  selectedVariant,
  busy,
  presenzaScaduta,
  senzaRete,
  onSit,
  onCancel,
}: Props) {
  const { t } = useTranslation();

  /*
   * Il proprio tavolo esce dal mazzo PRIMA di ogni raggruppamento: va fissato in
   * cima da solo, e lasciarlo anche dentro i gruppi lo disegnerebbe due volte.
   */
  const mine = useMemo(
    () => (myTableId ? tables.find((tbl) => tbl.id === myTableId) ?? null : null),
    [tables, myTableId],
  );
  const rest = useMemo(() => (mine ? tables.filter((tbl) => tbl.id !== mine.id) : tables), [tables, mine]);

  /*
   * A stable partition, never a sort: re-ordering would throw away the FIFO the
   * server sent, and that order is the queue itself. A `lobby:table-added`
   * arriving live is appended to the store, so it lands at the end of its own
   * group here without a fresh `lobby:list`.
   *
   * Lowercased on both sides for the reason `variantShortLabel` does it: variant
   * ids reach the UI in two casings — UPPERCASE enum values on game objects,
   * lowercase strings from the dropdowns.
   */
  const [sameVariant, otherVariants] = useMemo(() => {
    const selected = String(selectedVariant).toLowerCase();
    const same: LobbyTableInfo[] = [];
    const others: LobbyTableInfo[] = [];
    for (const table of rest) {
      (String(table.variant).toLowerCase() === selected ? same : others).push(table);
    }
    return [same, others];
  }, [rest, selectedVariant]);

  /*
   * Quante righe stanno sotto la TESTATA — non quante sono in tutto.
   *
   * Il proprio tavolo e' uscito dal mazzo prima del raggruppamento, quindi non
   * sta in nessuno dei due gruppi; a schermo pero' sta proprio li', fissato
   * sopra lo scorrevole. Non contarlo direbbe «0» con una riga a video, e
   * succede ogni volta che il solo tavolo aperto e' il tuo.
   */
  const sopra = (mine ? 1 : 0) + sameVariant.length;

  /*
   * L'avviso parla quando sotto la TESTATA non c'e' niente: e' l'unico stato
   * vuoto rimasto, e vale sia a lobby del tutto deserta sia quando il deserto e'
   * solo nella variante scelta. La coda la decide quello che c'e' sotto — niente
   * «oppure siediti» se non c'e' nessun altrove a cui mandare.
   *
   * Non mentre si aspetta al proprio tavolo: li' invita a premere una cadenza, e
   * le cadenze in quel momento non sono nemmeno a schermo.
   */
  const avvisa = sameVariant.length === 0 && !mine;

  const renderRows = (list: LobbyTableInfo[]) =>
    list.map((table) => (
      <LobbyTableRow
        key={table.id}
        table={table}
        // Sempre falso qui: il proprio tavolo e' gia' uscito dal mazzo sopra.
        isMine={false}
        isGuest={isGuest}
        busy={busy}
        onSit={onSit}
        onCancel={onCancel}
      />
    ));

  return (
    <section className="lobby-tables" aria-label={t('lobby.openTables')}>
      <h3 className="lobby-tables-title">
        {t('lobby.openTables')}
        {/* Il gruppo, non il totale: ogni voce conta le righe che le stanno
            sotto, e «Altre varianti» fa lo stesso col suo. Lo zero si scrive,
            non si nasconde: dice che QUESTO gruppo e' vuoto, e non puo' essere
            scambiato per una smentita delle righe piu' sotto, perche' quelle
            hanno sempre la loro testata col loro numero. */}
        <span className="lobby-tables-count">{sopra}</span>
      </h3>

      {/* Fuori dallo scorrevole, cosi' non scorre mai via: e' il posto che aveva
          la card, senza esserne una copia. */}
      {mine && (
        <ul className="lobby-rows lobby-rows-mine">
          <LobbyTableRow
            table={mine}
            isMine
            isGuest={isGuest}
            busy={busy}
            onSit={onSit}
            onCancel={onCancel}
          />
        </ul>
      )}

      {/* FUORI dallo scorrevole: introduce quello che viene dopo, e dentro se ne
          andrebbe via scorrendo proprio mentre si guardano le righe che sta
          spiegando. */}
      {/*
        * A presenza scaduta non si dice «nessun tavolo in italiano, premi una
        * cadenza per aprire il tuo»: sarebbe l'invito a un gesto impossibile,
        * sopra una lista vuota solo perche' non si sa piu' niente. Si dice il
        * fatto, e sono due fatti diversi — il dispositivo senza rete lo
        * dichiara, il socket caduto dietro un portale captive no.
        *
        * Il primo lo dice `AvvisoSenzaRete`, lo stesso avviso che l'app usa
        * ovunque per questo (titolo e corpo su due righe, icona via sotto i
        * 420px). Il secondo resta una riga nello stesso <p> di prima.
        */}
      {avvisa && (
        presenzaScaduta
          ? (senzaRete
            ? <AvvisoSenzaRete contesto="dati" />
            : <p className="lobby-variant-notice" role="status">{t('online.connecting')}</p>)
          : (
            <p className="lobby-variant-notice" role="status">
              {t(
                otherVariants.length > 0 ? 'lobby.noTablesInVariantOrOther' : 'lobby.noTablesInVariant',
                { variant: variantShortLabel(t, selectedVariant) },
              )}
            </p>
          )
      )}

      {/* One scroller around both lists. Left on `.lobby-rows`, two sibling
          <ul> would each scroll on their own. */}
      {rest.length > 0 && (
        <div className="lobby-rows-scroll">
          {sameVariant.length > 0 && <ul className="lobby-rows">{renderRows(sameVariant)}</ul>}

          {/* Testata e lista in un fiato: le righe di un'altra variante non
              restano mai orfane della voce che le nomina — e' quella voce a
              rendere leggibile uno «0» qui sopra. */}
          {otherVariants.length > 0 && (
            <>
              <h4 className="lobby-tables-subhead">
                {t('lobby.otherVariants')}
                <span className="lobby-tables-count">{otherVariants.length}</span>
              </h4>
              <ul className="lobby-rows">{renderRows(otherVariants)}</ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
